import React, { useState, useEffect, useMemo } from "react";
import { Card, TextInput, Button, Stack, Text, LoadingOverlay, Flex, ActionIcon, Divider, Box, Code, Badge, Modal, Loader } from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";
import { IconArrowRight, IconCircleFilled } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { PaginatedList } from "@components/shared";
import styled from "styled-components";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import hogLogo from "@assets/images/hog-logo-color.png";

const steps = {
  user: "user",
  create: "create",
  membership: "membership",
};

const buttonStyles = {
  color: "orange",
};

export default function HogApiMembershipCreate() {
  const [step, setStep] = useState(steps.user);
  const [user, setUser] = useState(null);
  const [membershipInfo, setMembershipInfo] = useState(null);
  const [refreshUserLookup, setRefreshUserLookup] = useState(false);

  // if (step === steps.create) {
  //   return <CreateFlow user={user} setUser={() => {
  //     setUser(null);
  //     setStep(steps.user);
  //   }} setMembershipInfo={(membershipInfo) => {
  //     setMembershipInfo(membershipInfo);
  //     setStep(steps.membership);
  //   }} />;
  // }

  // if (step === steps.membership) {
  //   return <MembershipFlow user={user} membershipInfo={membershipInfo} onDone={() => {
  //     setMembershipInfo(null);
  //     setUser(null);
  //     setStep(steps.user);
  //   }} />;
  // }

  function onUserClose() {
    setUser(null);
    setStep(steps.user);
  }

  function onMembershipClose() {
    setMembershipInfo(null);
    setUser(null);
    setStep(steps.user);
    setRefreshUserLookup(!refreshUserLookup);
  }

  return (
    <div>
      {user && !membershipInfo && <CreateFlow user={user} setUser={() => {
        onUserClose()
      }} setMembershipInfo={(membershipInfo) => {
        setMembershipInfo(membershipInfo);
        setStep(steps.membership);
      }} />}
      {membershipInfo && <MembershipFlow user={user} membershipInfo={membershipInfo} onDone={() => {
        onMembershipClose()
      }} />}
      <UserLookup refreshUserLookup={refreshUserLookup} setUser={(userData) => {
        if (userData) {
          setUser(userData);
          setStep(steps.create);
        } else {
          setUser(null);
          setStep(steps.user);
        }
      }} />
    </div>
  )
}

const UserLookup = ({ refreshUserLookup, setUser }) => {
  const [searchValue, setSearchValue] = useState("");

  const userLookupQuery = useQuery({
    queryKey: ["admin-user-lookup", searchValue, refreshUserLookup],
    queryFn: async ({ queryKey: [, searchVal], signal }) => {
      const response = await axios.post(
        `/loyalty-memberships/admin-user-lookup/`,
        { search_value: searchVal },
        { signal }
      );
      return response.data.response;
    },
    enabled: searchValue.length >= 3,
    placeholderData: (previousData) => previousData,
  });

  const { data, isFetching, isError, refetch } = userLookupQuery;

  return <div>
    <Flex gap="xs" align="center" mb="xs">
      <Text fw={600} size="md">Search Harley Organization Users by first name, last name, email, or phone number</Text>
      {isFetching && <Loader size="sm" />}
    </Flex>
    <Flex gap="xs" mb="sm" align="center">
      <TextInput
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder="Search users"
        size="md"
        style={{ flexGrow: 1 }}
      // onKeyDown={(e) => {
      //   if (e.key === "Enter" && searchValue.length >= 3) {
      //     refetch();
      //   }
      // }}
      />
      {/* <ActionIcon loading={isFetching} onClick={() => refetch()} size="lg" color="orange" disabled={searchValue.length < 3 || isFetching}>
        <IconArrowRight size={16} />
      </ActionIcon> */}
    </Flex>
    {data?.length > 0 && <>
      <Stack gap="xs">
        {data.map((result) => <Card key={result.id}>
          <Flex gap="xs" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }}>
            <div style={{ flexGrow: 1 }}>
              <Flex gap="xs" align={{ base: "flex-start", md: "center" }}>
                <Text fw={600}>{result.first_name} {result.last_name}</Text>
                <ActionIcon radius="xl" color={entityColor.user} variant="light" component={Link} to={`/users/${result.mightyloud_id}`} target="_blank" title="Mighty Loud User">{entityIcon.user(16)}</ActionIcon>
                <ActionIcon radius="xl" color={entityColor.organization} variant="light" component={Link} to={`/user-organizations/${result.user_organization_id}`} target="_blank" title="Harley Organization User">{entityIcon.organization(16)}</ActionIcon>
              </Flex>
              <Text>{result.email}</Text>
              <Text>{result.mobile_phone}</Text>
              {result.hog_membership && <Box mt="xs">
                <StyledHogInfo>
                  <Flex gap="xs" align={{
                    base: "flex-start",
                    md: "center",
                  }} direction={{ base: "column", md: "row" }}>
                    <IconCircleFilled title={result.hog_membership.is_active ? "Active" : "Inactive"} size={16} style={{
                      color: result.hog_membership.is_active ? "var(--mantine-color-green-6)" : "var(--mantine-color-red-6)",
                    }} />
                    <img src={hogLogo} alt="HOG Logo" />
                    <Text fw={600} c="white">{result.hog_membership.ident}</Text>
                    <Text c="dimmed" size="xs">last verified {new Date(result.hog_membership.last_verified_at).toLocaleDateString()}</Text>
                  </Flex>
                </StyledHogInfo>
              </Box>}
            </div>
            {!result.has_hog_membership && <Button onClick={() => setUser(result)} {...buttonStyles}>Create</Button>}
          </Flex>
        </Card>)}
      </Stack>
    </>}
    {(data?.length === 0 || isError) && !isFetching && searchValue.length > 0 && <Text c="dimmed">No results found</Text>}
  </div>;
}

const CreateFlow = ({ user, setUser, setMembershipInfo }) => {
  const [formValues, setFormValues] = useState({
    email: "",
  });
  const [createFailure, setCreateFailure] = useState(false);

  useEffect(() => {
    setFormValues({
      email: user.email || "",
    });
  }, [JSON.stringify(user)]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/loyalty-memberships/admin-create-third-party-loyalty-membership/`, {
        ...user,
        email: formValues.email,
      });
      return response.data.response[0];
    },
    onSuccess: (res) => {
      if (res.success === false) {
        setCreateFailure(res);
      } else {
        setMembershipInfo(res);
      }
    },
    onError: (error) => {
      toast.error(error)
    },
  });

  const emailValid = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email);
  }, [formValues.email]);

  return <Modal opened onClose={() => {
    if (submitMutation.isPending) {
      return;
    }
    setUser(null);
  }}>
    {createFailure && <div>
      <MembershipCreateFailure response={createFailure} user={{
        ...user,
        email: formValues.email,
      }} />
      <Button fullWidth mt="sm" {...buttonStyles} onClick={() => setCreateFailure(null)}>Try again</Button>
    </div>}
    {!createFailure && <div style={{ position: "relative" }}>
      <Stack gap="md">
        <Text fw={600} size="lg" align="center">Are you sure you want to create a HOG membership for this user?</Text>
        <Box p="sm" style={{
          backgroundColor: "var(--mantine-color-gray-0)",
          border: "1px solid var(--mantine-color-gray-2)",
          borderRadius: "var(--mantine-radius-md)",
        }}>
          <UserInfo user={user} />
          <TextInput
            value={formValues.email}
            onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
            placeholder="Email"
            size="sm"
            style={{ flexGrow: 1 }}
            mt="xs"
            label="Email"
          />
        </Box>
        <Button {...buttonStyles} onClick={() => submitMutation.mutate()} disabled={!formValues.email || !emailValid}>Create Membership</Button>
        <Button {...buttonStyles} color="gray" variant="subtle" onClick={() => setUser(null)}>Cancel</Button>
      </Stack>
      <LoadingOverlay visible={submitMutation.isPending} />
    </div>}
  </Modal>
}

const MembershipCreateFailure = ({ response, user }) => {
  return <div>
    <Text c="red" fw={600} mb="sm" align="center">Membership creation failed</Text>
    <Box p="sm" style={{
      backgroundColor: "var(--mantine-color-gray-0)",
      border: "1px solid var(--mantine-color-gray-2)",
      borderRadius: "var(--mantine-radius-md)",
    }}>
      <UserInfo user={user} showEmail={true} />
    </Box>
    <Divider my="sm" />
    <Text fw={600} mb="xs">Response from API</Text>
    <Code block>{JSON.stringify(response, null, 2)}</Code>
  </div>;
}

const MembershipFlow = ({ membershipInfo, user, onDone }) => {
  return <Modal opened onClose={() => onDone()}>
    <Stack gap="sm">
      <Text fw={600} align="center">Membership created successfully!</Text>
      <Box p="sm" style={{
        backgroundColor: "var(--mantine-color-gray-0)",
        border: "1px solid var(--mantine-color-gray-2)",
        borderRadius: "var(--mantine-radius-md)",
      }}>
        <UserInfo user={user} />
        <Divider my="sm" />
        <MembershipInfo membershipInfo={membershipInfo} />
      </Box>
      <Button {...buttonStyles} onClick={onDone}>Search for another user</Button>
    </Stack>
  </Modal>;
}

const UserInfo = ({ user, showEmail = false }) => {
  return <>
    <Flex gap="xs" align="center">
      <Text fw={600}>{user.first_name} {user.last_name}</Text>
      <ActionIcon radius="xl" color={entityColor.user} variant="light" component={Link} to={`/users/${user.mightyloud_id}`} target="_blank" title="Mighty Loud User">{entityIcon.user(16)}</ActionIcon>
      <ActionIcon radius="xl" color={entityColor.organization} variant="light" component={Link} to={`/user-organizations/${user.user_organization_id}`} target="_blank" title="Harley Organization User">{entityIcon.organization(16)}</ActionIcon>
    </Flex>
    <Text>{user.mobile_phone}</Text>
    <Text>{user.country || "Country not found"}</Text>
    {showEmail && <Text>{user.email}</Text>}
  </>;
}

const MembershipInfo = ({ membershipInfo }) => {

  const loyaltyMembership = membershipInfo.loyalty_membership;
  const apiResponse = membershipInfo.api_response;

  return <div>
    {loyaltyMembership && <>
      <div>
        <Text fw={600}>{loyaltyMembership.ident}</Text>
        <Text>{loyaltyMembership.is_active ? "Active" : "Inactive"}</Text>
        <Text>Activated {new Date(loyaltyMembership.initial_activation_date).toLocaleDateString()}</Text>
        <Text>Last verified {new Date(loyaltyMembership.last_verified_at).toLocaleDateString()}</Text>
      </div>
    </>}
    {loyaltyMembership && apiResponse && <Divider my="sm" />}
    {apiResponse && <div>
      <Text fw={600}>API Response</Text>
      <Code block>{JSON.stringify(apiResponse, null, 2)}</Code>
    </div>}
  </div>;
}


const StyledHogInfo = styled.div`
  background-color: var(--mantine-color-dark-8);
  border-radius: var(--mantine-radius-md);
  padding: 5px 10px;
  
  img {
    width: 50px;
    height: 16px;
    object-fit: contain;
  }
`;
